import { defineStore } from 'pinia'
import { ref, shallowRef } from 'vue'
import type { Howl } from 'howler'
import type { MyGeneratedMusicItem } from '~/types/generate'
import type { ParsedClipItem } from '~/types/home'
import { addPlayCount } from '~/apis/app'
import { useMySongsStore } from '~/stores/mySongsStore'

let howlerModulePromise: Promise<typeof import('howler')> | null = null

const importHowlerModule = () => {
  if (!howlerModulePromise) {
    howlerModulePromise = import('howler')
  }
  return howlerModulePromise
}

const isPerfDebugEnabled = () => {
  if (typeof window === 'undefined') return false
  try {
    if (window.localStorage?.getItem('perf_debug') === '1') return true
  } catch {}
  return !!(process as any).dev
}

export const usePlayerStore = defineStore('PlayerStore', () => {
  const currentSong = shallowRef<ParsedClipItem | MyGeneratedMusicItem | null>(null)
  const currentSongIndex = ref(-1) // 当前播放歌曲在列表中的索引
  const songsList = shallowRef<(ParsedClipItem | MyGeneratedMusicItem)[]>([]) // 播放列表
  const isPlaying = ref(false)
  const currentTime = ref(0) // 当前播放时间（秒）
  const duration = ref(0) // 总时长（秒）
  const howlInstance = shallowRef<Howl | null>(null)
  const volume = ref(1) // 音量 0-1
  const currentAudioUrl = ref<string | null>(null) // 当前使用的音频 URL
  const fallbackUrls = ref<string[]>([]) // 备用 URL 列表
  const playMode = ref<'order' | 'single_loop'>('order') // 播放模式：顺序播放、单曲循环
  const hasReportedPlayCountForCurrent = ref(false) // 当前歌曲是否已上报过播放量（本次播放）
  const isLoadingAudio = ref(false)

  const warmupHowler = () => {
    if (typeof window === 'undefined') return
    void importHowlerModule().catch(() => {})
  }

  // 尝试使用备用 URL
  const tryFallbackUrl = async () => {
    if (fallbackUrls.value.length === 0) {
      console.error('No fallback URLs available')
      return
    }

    const nextUrl = fallbackUrls.value[0]
    if (!nextUrl) {
      console.error('Fallback URL is empty')
      return
    }

    fallbackUrls.value = fallbackUrls.value.slice(1)
    currentAudioUrl.value = nextUrl

    // 使用备用 URL 创建新的 Howl 实例
    await createHowlInstance(nextUrl)
  }

  // 播放下一首
  const playNext = async () => {
    if (songsList.value.length > 0 && currentSongIndex.value >= 0) {
      const nextIndex = currentSongIndex.value + 1
      if (nextIndex < songsList.value.length) {
        const nextSong = songsList.value[nextIndex]
        if (nextSong) {
          currentSongIndex.value = nextIndex
          await playSong(nextSong)
        }
      }
    }
  }

  const switchCurrentSongSource = async (
    updatedSong: ParsedClipItem | MyGeneratedMusicItem
  ) => {
    if (!currentSong.value || !updatedSong || currentSong.value.uuid !== updatedSong.uuid) {
      return
    }

    const fileInfo = (updatedSong as ParsedClipItem).fileInfo || (updatedSong as MyGeneratedMusicItem).fileInfo
    const urls: string[] = []

    if (fileInfo) {
      if (fileInfo.mp3Url && typeof fileInfo.mp3Url === 'string') {
        urls.push(fileInfo.mp3Url)
      }
      if (fileInfo.mp4Url && typeof fileInfo.mp4Url === 'string' && !urls.includes(fileInfo.mp4Url)) {
        urls.push(fileInfo.mp4Url)
      }
      if (fileInfo.cosUrl && typeof fileInfo.cosUrl === 'string' && !urls.includes(fileInfo.cosUrl)) {
        urls.push(fileInfo.cosUrl)
      }
    }

    if (urls.length === 0) {
      currentSong.value = updatedSong
      const updatedDuration = Number((fileInfo as any)?.duration || 0)
      if (!duration.value && Number.isFinite(updatedDuration) && updatedDuration > 0) {
        duration.value = updatedDuration
      }
      return
    }

    let primaryUrl = urls[0]
    let remainingFallbacks = urls.slice(1)

    if (primaryUrl) {
      try {
        new URL(primaryUrl)
      } catch {
        console.error('Invalid audio URL format when switching source:', primaryUrl)
        if (remainingFallbacks.length > 0) {
          primaryUrl = remainingFallbacks[0]
          remainingFallbacks = remainingFallbacks.slice(1)
        } else {
          return
        }
      }
    }

    if (!primaryUrl || primaryUrl === currentAudioUrl.value) {
      currentSong.value = updatedSong
      const updatedDuration = Number((fileInfo as any)?.duration || 0)
      if (!duration.value && Number.isFinite(updatedDuration) && updatedDuration > 0) {
        duration.value = updatedDuration
      }
      return
    }

    const wasPlaying = isPlaying.value
    const previousTime = currentTime.value

    fallbackUrls.value = remainingFallbacks
    currentAudioUrl.value = primaryUrl

    await createHowlInstance(primaryUrl, {
      onReady: (howl) => {
        const targetDuration = howl.duration() || 0
        const clampedTime = Math.max(0, Math.min(previousTime, targetDuration || previousTime))
        if (clampedTime > 0) {
          howl.seek(clampedTime)
          currentTime.value = clampedTime
        }
        if (wasPlaying) {
          howl.play()
        }
      }
    })

    currentSong.value = updatedSong
  }

  // 创建 Howl 实例
  const createHowlInstance = async (url: string, options?: { onReady?: (howl: Howl) => void }) => {
    // 只在客户端执行
    if (typeof window === 'undefined') return

    const perfOn = isPerfDebugEnabled()
    const perfStart = perfOn ? performance.now() : 0

    isLoadingAudio.value = true

    // 清理现有实例
    if (howlInstance.value) {
      howlInstance.value.unload()
    }

    // 动态导入客户端专用库
    const importStart = perfOn ? performance.now() : 0
    const { Howl } = await importHowlerModule()
    if (perfOn) {
      console.log('[perf][playerStore] howler import ms:', (performance.now() - importStart).toFixed(1))
    }

    // 创建新的 Howl 实例
    const howlCreateStart = perfOn ? performance.now() : 0
    const howl = new Howl({
      src: [url],
      html5: true, // 使用 HTML5 Audio 以便更好地处理流媒体
      preload: true,
      volume: volume.value,
      format: ['mp3'],
      onload: () => {
        // 音频加载完成
        duration.value = howl.duration() || 0
        isLoadingAudio.value = false
        if (options?.onReady) {
          options.onReady(howl)
        }
      },
      onloaderror: (id, error) => {
        console.error('Howl load error:', {
          id,
          error,
          url
        })

        isPlaying.value = false

        // 尝试使用备用 URL
        if (fallbackUrls.value.length > 0) {
          tryFallbackUrl()
        } else {
          isLoadingAudio.value = false
        }
      },
      onplay: () => {
        isPlaying.value = true
        // 开始更新当前时间
        updateCurrentTime()
      },
      onpause: () => {
        isPlaying.value = false
        stopUpdatingCurrentTime()
      },
      onend: () => {
        isPlaying.value = false
        stopUpdatingCurrentTime()

        if (playMode.value === 'single_loop') {
          // 单曲循环：重置时间并重新播放
          currentTime.value = 0
          howlInstance.value?.seek(0)
          howlInstance.value?.play()
        } else {
          // 顺序播放：播放下一首
          currentTime.value = 0
          playNext()
        }
      },
      onseek: () => {
        // 当 seek 完成时更新当前时间
        if (howlInstance.value) {
          currentTime.value = howlInstance.value.seek() || 0
        }
      },
      onvolume: () => {
        // 当音量改变时更新音量状态
        volume.value = howl.volume()
      },
      onplayerror: (id, error) => {
        console.error('Howl play error:', {
          id,
          error,
          url
        })

        isPlaying.value = false

        // 尝试使用备用 URL
        if (fallbackUrls.value.length > 0) {
          tryFallbackUrl()
        } else {
          isLoadingAudio.value = false
        }
      }
    })

    howlInstance.value = howl

    if (perfOn) {
      console.log('[perf][playerStore] new Howl ms:', (performance.now() - howlCreateStart).toFixed(1))
      console.log('[perf][playerStore] createHowlInstance total ms:', (performance.now() - perfStart).toFixed(1))
    }
  }

  // 用于更新当前播放时间的定时器
  let timeUpdateInterval: number | null = null
  let timeUpdatesSuspended = false
  let lastPlayCountCheckAt = 0

  const resolveTimeUpdateIntervalMs = () => {
    if (typeof window === 'undefined' || typeof navigator === 'undefined') return 500
    const nav = navigator as Navigator & { msMaxTouchPoints?: number }
    const maxTouchPoints = Number(nav.maxTouchPoints || nav.msMaxTouchPoints || 0)
    if (Number.isFinite(maxTouchPoints) && maxTouchPoints > 0) return 500
    try {
      if (typeof window.matchMedia === 'function' && window.matchMedia('(pointer: coarse)').matches) return 500
    } catch {}
    return 100
  }

  // 检查是否需要上报播放量（播放满 30 秒）
  const checkAndReportPlayCount = async () => {
    if (hasReportedPlayCountForCurrent.value) return
    if (!currentSong.value) return

    const uuid = (currentSong.value as any).uuid as string | undefined
    if (!uuid) return

    if (currentTime.value < 30) return

    hasReportedPlayCountForCurrent.value = true

    try {
      await addPlayCount({ uuid: String(uuid) })
    } catch (error) {
      console.error('Failed to add play count:', {
        uuid,
        error
      })
    }

    // 同步更新 mySongsStore（Generate / Library 列表）
    try {
      const mySongsStore = useMySongsStore()
      mySongsStore.updateSong(uuid, (song) => {
        const prev = typeof song.play_count === 'number' ? song.play_count : 0
        return {
          ...song,
          play_count: prev + 1
        }
      })
    } catch (error) {
      console.warn('Failed to sync play_count to mySongsStore:', error)
    }

    // 通知其他模块（如首页 New Songs / Trending / Recommended / 右侧详情）该 uuid 的播放量已增加
    if (typeof window !== 'undefined') {
      try {
        window.dispatchEvent(new CustomEvent('song-play-count-updated', {
          detail: { uuid: String(uuid) }
        }))
      } catch (error) {
        console.warn('Failed to dispatch song-play-count-updated event:', error)
      }
    }
  }

  // 开始更新当前时间
  const updateCurrentTime = () => {
    // 只在客户端执行
    if (typeof window === 'undefined') return
    if (timeUpdatesSuspended) return
    stopUpdatingCurrentTime() // 确保只有一个定时器在运行
    timeUpdateInterval = window.setInterval(() => {
      if (howlInstance.value && isPlaying.value) {
        currentTime.value = howlInstance.value.seek() || 0
        // 播放进度更新时检查是否需要上报播放量（节流，避免频繁占用主线程）
        const now = Date.now()
        if (now - lastPlayCountCheckAt >= 1000) {
          lastPlayCountCheckAt = now
          void checkAndReportPlayCount()
        }
      }
    }, resolveTimeUpdateIntervalMs())
  }

  // 停止更新当前时间
  const stopUpdatingCurrentTime = () => {
    if (timeUpdateInterval) {
      clearInterval(timeUpdateInterval)
      timeUpdateInterval = null
    }
  }

  const suspendTimeUpdates = () => {
    timeUpdatesSuspended = true
    stopUpdatingCurrentTime()
  }

  const resumeTimeUpdates = () => {
    timeUpdatesSuspended = false
    if (howlInstance.value && isPlaying.value) {
      updateCurrentTime()
    }
  }

  // 播放歌曲（带播放列表）
  const playSong = async (
    song: ParsedClipItem | MyGeneratedMusicItem,
    songsListParam?: (ParsedClipItem | MyGeneratedMusicItem)[]
  ) => {
    if (!song) return

    const perfOn = isPerfDebugEnabled()
    const perfStart = perfOn ? performance.now() : 0
    const uuid = (song as any)?.uuid
    if (perfOn) {
      console.log('[perf][playerStore] playSong start:', uuid)
    }

    songsListParam = songsListParam?.filter(listItem => listItem.status !== 4 && !listItem.metadata?.refund_credits)

    // 如果提供了播放列表，更新列表和索引
    if (songsListParam && songsListParam.length > 0) {
      songsList.value = songsListParam
      const index = songsListParam.findIndex(s => s.uuid === song.uuid)
      currentSongIndex.value = index >= 0 ? index : 0
    } else if (songsList.value.length > 0) {
      // 如果没有提供列表但已有列表，尝试在现有列表中查找
      const index = songsList.value.findIndex(s => s.uuid === song.uuid)
      if (index >= 0) {
        currentSongIndex.value = index
      }
    }

    // 如果切换歌曲，需要创建新的 Howl 实例
    if (currentSong.value?.uuid !== song.uuid) {
      // 重置当前歌曲的播放量上报标记
      hasReportedPlayCountForCurrent.value = false

      // 收集所有可能的音频 URL（优先使用 mp3Url）
      const fileInfo = (song as ParsedClipItem).fileInfo || (song as MyGeneratedMusicItem).fileInfo
      const urls: string[] = []

      if (fileInfo?.mp3Url && typeof fileInfo.mp3Url === 'string') {
        urls.push(fileInfo.mp3Url)
      } else {
        urls.push(song.stream_url)
      }

      if (urls.length === 0) {
        console.error('No audio URL found for song:', {
          song: song.title || song.uuid,
          fileInfo
        })
        return
      }

      // 保存备用 URL
      fallbackUrls.value = urls.slice(1) // 除了第一个 URL 之外的所有 URL
      const primaryUrl = urls[0]
      currentAudioUrl.value = primaryUrl || null

      // 验证 URL 格式
      if (primaryUrl) {
        try {
          new URL(primaryUrl)
        } catch {
          console.error('Invalid audio URL format:', primaryUrl)
          // 如果第一个 URL 无效，尝试下一个
          if (fallbackUrls.value.length > 0) {
            const nextUrl = fallbackUrls.value[0]
            currentAudioUrl.value = nextUrl || null
            fallbackUrls.value = fallbackUrls.value.slice(1)
          } else {
            return
          }
        }
      }

      if (!currentAudioUrl.value) {
        console.error('No valid audio URL available')
        return
      }

      // 提前更新 UI 侧的当前歌曲（避免 await 初始化音频期间 UI 不响应导致卡顿感）
      currentSong.value = song
      currentTime.value = 0
      duration.value = 0

      // 创建新的 Howl 实例
      const beforeHowl = perfOn ? performance.now() : 0
      await createHowlInstance(currentAudioUrl.value)
      if (perfOn) {
        console.log('[perf][playerStore] await createHowlInstance ms:', (performance.now() - beforeHowl).toFixed(1))
      }

      howlInstance.value?.play()

      if (perfOn) {
        console.log('[perf][playerStore] playSong total ms:', (performance.now() - perfStart).toFixed(1))
      }
    } else {
      // 同一首歌，直接播放/暂停
      if (howlInstance.value) {
        if (isPlaying.value) {
          howlInstance.value.pause()
        } else {
          howlInstance.value.play()
        }
      }
    }
  }

  // 暂停播放
  const pause = () => {
    howlInstance.value?.pause()
  }

  // 继续播放
  const resume = async () => {
    howlInstance.value?.play()
  }

  // 设置播放时间
  const seek = (time: number) => {
    const clampedTime = Math.max(0, Math.min(time, duration.value))
    howlInstance.value?.seek(clampedTime)
    currentTime.value = clampedTime
  }

  // 设置音量
  const setVolume = (vol: number) => {
    const clampedVol = Math.max(0, Math.min(1, vol))
    volume.value = clampedVol
    howlInstance.value?.volume(clampedVol)
  }

  // 播放上一首
  const playPrevious = async () => {
    if (songsList.value.length > 0 && currentSongIndex.value >= 0) {
      const prevIndex = currentSongIndex.value - 1
      if (prevIndex >= 0) {
        const prevSong = songsList.value[prevIndex]
        if (prevSong) {
          currentSongIndex.value = prevIndex
          await playSong(prevSong)
        }
      }
    }
  }

  // 清理资源
  const cleanup = () => {
    // 停止更新当前时间
    stopUpdatingCurrentTime()

    // 清理 Howl 实例
    if (howlInstance.value) {
      howlInstance.value.unload()
      howlInstance.value = null
    }

    // 重置状态
    currentSong.value = null
    currentSongIndex.value = -1
    songsList.value = []
    currentAudioUrl.value = null
    fallbackUrls.value = []
    isPlaying.value = false
    isLoadingAudio.value = false
    currentTime.value = 0
    duration.value = 0
    playMode.value = 'order' // 重置为顺序播放
    hasReportedPlayCountForCurrent.value = false
  }

  /**
   * 当歌曲被删除时同步更新播放器状态：
   * - 如果删除的是当前播放歌曲，则自动切到下一首（或上一首），否则清空播放器
   * - 同时从播放列表中移除该歌曲，更新索引
   */
  const handleSongDeleted = async (uuid: string) => {
    if (!uuid) return

    const isCurrentSong = currentSong.value?.uuid === uuid
    const deletedIndex = songsList.value.findIndex(song => song.uuid === uuid)

    // 如果既不是当前播放歌曲，也不在播放列表中，直接忽略
    if (!isCurrentSong && deletedIndex === -1) {
      return
    }

    const newSongsList = songsList.value.filter(song => song.uuid !== uuid)

    // 删除的不是当前播放歌曲，仅同步播放列表
    if (!isCurrentSong) {
      songsList.value = newSongsList

      // 如果当前索引在删除项之后，索引需要前移一位
      if (deletedIndex !== -1 && currentSongIndex.value > deletedIndex) {
        currentSongIndex.value = currentSongIndex.value - 1
      }

      if (songsList.value.length === 0) {
        currentSongIndex.value = -1
      }
      return
    }

    // 删除的是当前播放歌曲
    if (newSongsList.length === 0) {
      // 播放列表中已经没有其他歌曲，直接清空播放器
      cleanup()
      return
    }

    // 还有其他歌曲时：优先播放当前索引之后的下一首；如果被删的是最后一首，则播放新的最后一首
    let nextIndex = 0

    if (deletedIndex !== -1) {
      if (deletedIndex < newSongsList.length) {
        // 原来后面还有歌，切到原来的下一首
        nextIndex = deletedIndex
      } else {
        // 被删的是原来的最后一首，切到新的最后一首
        nextIndex = newSongsList.length - 1
      }
    } else {
      // 当前歌曲不在播放列表中，但仍然要切歌，默认切到第一首
      nextIndex = 0
    }

    const nextSong = newSongsList[nextIndex]
    if (!nextSong) {
      cleanup()
      return
    }

    songsList.value = newSongsList
    currentSongIndex.value = nextIndex
    await playSong(nextSong)
  }

  // 切换播放模式
  const togglePlayMode = () => {
    playMode.value = playMode.value === 'order' ? 'single_loop' : 'order'
  }

  return {
    currentSong,
    currentSongIndex,
    songsList,
    isPlaying,
    isLoadingAudio,
    currentTime,
    duration,
    volume,
    playMode,
    howlInstance,
    playSong,
    pause,
    resume,
    seek,
    setVolume,
    playNext,
    playPrevious,
    togglePlayMode,
    switchCurrentSongSource,
    warmupHowler,
    cleanup,
    handleSongDeleted,
    suspendTimeUpdates,
    resumeTimeUpdates
  }
})
